import type { FC } from "react";

import { Navigate, NoContextOutlet, type RouteObject } from "src/router";
import { useFlags } from "launchdarkly-react-client-sdk";
import { NotFound } from "src/pages/not-found";
import { FeatureFlagRoute } from "src/components/router/feature-flag-route";
import { lazyNamed } from "src/utils/lazy-named";
import type { RoutePreload } from "src/router/route-preloads";
const SubsetCategories = lazyNamed(
  () => import("./subsets/subset-categories"),
  "SubsetCategories",
);
import { FormErrorProvider } from "src/contexts/form-error-context";

const governanceLayoutImport = () => import("./layout");
const Layout = lazyNamed(governanceLayoutImport, "Layout");

export const preloads: RoutePreload[] = [
  ["/governance", governanceLayoutImport],
];
const DestinationRules = lazyNamed(
  () => import("./destination-rules/destination-rules"),
  "DestinationRules",
);
const DestinationRule = lazyNamed(
  () => import("./destination-rules/destination-rule"),
  "DestinationRule",
);
const CreateCategory = lazyNamed(
  () => import("./subsets/category/create-category"),
  "CreateCategory",
);
const ViewCategory = lazyNamed(
  () => import("./subsets/category/view-category"),
  "ViewCategory",
);
const Subset = lazyNamed(() => import("./subsets/subset"), "Subset");
const DataMaskingTable = lazyNamed(
  () => import("./privacy/data-masking-table"),
  "DataMaskingTable",
);
const DataCollaboration = lazyNamed(
  () => import("./data-collaboration"),
  "DataCollaboration",
);

// Guard component to check if governance feature is enabled
const GovernanceGuard: FC = () => {
  const { appDestinationRulesEnabled } = useFlags();

  if (!appDestinationRulesEnabled) {
    return <NotFound />;
  }

  return <NoContextOutlet />;
};

const SubsetCategoriesWrapper: FC = () => {
  return <SubsetCategories />;
};

export const routes: Array<RouteObject> = [
  {
    path: "governance/*",
    element: <GovernanceGuard />,
    spacesAccess: "hidden-from-limited-view",
    children: [
      {
        path: "",
        element: <Layout />,
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            element: (
              <Navigate
                to={{
                  pathname: "destination-rules",
                  search: window.location.search,
                }}
                replace
              />
            ),
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "destination-rules",
            element: <DestinationRules />,
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                path: "new",
                element: <DestinationRule />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: ":destination_rule_id",
                element: <DestinationRule />,
                spacesAccess: "hidden-from-limited-view",
              },
            ],
          },
          {
            path: "data-masking",
            element: (
              <FeatureFlagRoute
                flagKey="appPrivacyFeatures"
                redirect="/governance"
              >
                <DataMaskingTable />
              </FeatureFlagRoute>
            ),
            spacesAccess: "hidden-from-limited-view",
          },
          {
            path: "data-collaboration",
            element: (
              <FeatureFlagRoute
                flagKey="appEnableGartnerDemo"
                redirect="/governance"
              >
                <DataCollaboration />
              </FeatureFlagRoute>
            ),
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                path: "new",
                element: (
                  <FeatureFlagRoute
                    flagKey="appEnableGartnerDemo"
                    redirect="/governance"
                  >
                    <DataCollaboration />
                  </FeatureFlagRoute>
                ),
                spacesAccess: "hidden-from-limited-view",
              },
            ],
          },
          {
            path: "subset-categories",
            element: <SubsetCategoriesWrapper />,
            spacesAccess: "hidden-from-limited-view",
            children: [
              {
                path: "new",
                element: <CreateCategory />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: ":subset_category_id",
                element: <ViewCategory />,
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: ":subset_category_id/subsets/new",
                element: (
                  <FormErrorProvider>
                    <Subset />
                  </FormErrorProvider>
                ),
                spacesAccess: "hidden-from-limited-view",
              },
              {
                path: ":subset_category_id/subsets/:subset_id",
                element: (
                  <FormErrorProvider>
                    <Subset />
                  </FormErrorProvider>
                ),
                spacesAccess: "hidden-from-limited-view",
              },
            ],
          },
        ],
      },
    ],
  },
];
